import { getAddonCatalog, getDemographicCatalog } from "common";
import { some } from "lodash";
import {
    Experience,
    LineItemTemplate,
    ProductCatalogItem,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleLink,
    PurchaseRuleShortcutType,
} from "types";
import { PackagePricingValues } from "./EditPackageBasicDiscount";
import {
    BasicDiscountAddonTerm,
    BasicDiscountTerm,
    getCatalogByItem,
    getDefaultAction,
    getPackageDiscountAction,
    getPurchaseAction,
    isAdvancedPricing,
    isBasicPricing,
    TermsType,
} from "./PricingSelectorHelper";
import { isAddonCatalogItem, isChoiceAddOn } from "@/models/CatalogItem";
import { getPurchaseRuleForExperience } from "@/models/PurchaseRule";

/**
 * Returns the updated Payload of the partnerPackage / Package purchaseRule
 * Note: This function is being used by both partnerpackages and packages.
 */
export const updatePurchaseRulePayload = (
    selectedExperiences: Experience[],
    purchaseRule: PurchaseRule,
    originalPurchaseRule?: PurchaseRule,
) => {
    const experience = selectedExperiences.find((experience) => {
        return [purchaseRule.meta?.subProductId, purchaseRule.meta?.product].includes(experience.id);
    });

    if (experience) {
        const catalogs = [...getDemographicCatalog(experience), ...getAddonCatalog(experience)];
        const catalogIds = new Set(catalogs.map((catalog) => catalog.template?.id));

        // if catalogIds are removed or not present in experience then remove the action
        const updatedActions = purchaseRule.actions.map((actionItr) => {
            const actionTemplateId = actionItr.shortcut?.options?.templateIds?.[0] ?? "";
            // if action is already present but removed in experience
            const removeAction =
                actionItr.id &&
                !catalogIds.has(actionTemplateId) &&
                [
                    PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
                    PurchaseRuleActionObject.UPSERT_LINE_ITEM_ACTION,
                ].includes(actionItr.object);
            return removeAction ? { ...actionItr, _remove: true } : actionItr;
        });

        // If the action exists on original Purchase Rule and it doesn't exist on
        // Updated purchase rule, then we need to add _remove to it.
        const removedActions = [];
        for (const action of originalPurchaseRule?.actions ?? []) {
            if (!updatedActions.some((updatedAction) => updatedAction.id === action.id)) {
                removedActions.push({ ...action, _remove: true });
            }
        }

        return { ...purchaseRule, actions: [...removedActions, ...updatedActions] };
    }

    return purchaseRule;
};

/**
 * Note: This function is being used by both partnerpackages and packages.
 */
const populateDiscountAction = (
    catalogs: ProductCatalogItem[],
    discountPricingValue: PurchaseRuleAction[],
    values: PackagePricingValues,
    discountTemplate: LineItemTemplate | null,
    purchaseRule?: PurchaseRule,
) => {
    for (const catalog of catalogs) {
        const catalogItem = getCatalogByItem(catalogs, catalog) ?? catalog;
        const shouldExcludeAddons = values.basicDiscount.includeAddons === BasicDiscountAddonTerm.EXCLUDING_ADD_ONS;
        const discount = isAddonCatalogItem(catalogItem) && shouldExcludeAddons ? 0 : values.basicDiscount.discount;

        if (isChoiceAddOn(catalogItem)) {
            populateDiscountAction(
                catalog.items as ProductCatalogItem[],
                discountPricingValue,
                values,
                discountTemplate,
                purchaseRule,
            );
        } else {
            const purchaseAction = getPurchaseAction(purchaseRule, catalog.template?.id);
            const action = purchaseAction ?? getDefaultAction();
            const value = {
                shortcutType: PurchaseRuleShortcutType.PERCENT,
                discount: discount,
            };
            const discountAction = getPackageDiscountAction(value, action, catalogItem, discountTemplate);

            discountPricingValue.push(discountAction);
        }
    }
};

/**
 * Note: This function is being used by both partnerpackages and packages.
 */
export const updateBasicTermPurchaseRule = (
    values: PackagePricingValues,
    experiences: Experience[],
    purchaseRuleState: PurchaseRule[],
    discountTemplate: LineItemTemplate | null,
) => {
    const purchaseRuleStates: PurchaseRule[] = [];
    const shouldIncludeAddons = values.basicDiscount.includeAddons === BasicDiscountAddonTerm.INCLUDING_ADD_ONS;

    for (const experience of experiences) {
        const purchaseRule = getPurchaseRuleForExperience(purchaseRuleState, experience.id);
        const discountPricingValues: PurchaseRuleAction[] = [];

        // Update rules for all Demographics and addons in this experience
        const catalogs = [...getDemographicCatalog(experience), ...getAddonCatalog(experience)];

        populateDiscountAction(catalogs, discountPricingValues, values, discountTemplate, purchaseRule);

        const templateIds = new Set(discountPricingValues.map((action) => action.shortcut?.options?.templateIds?.[0]));

        if (purchaseRule) {
            const filteredActions = purchaseRule.actions.filter((actionItr) => {
                const actionTemplateId = actionItr.shortcut?.options?.templateIds?.[0] ?? "";
                return !templateIds.has(actionTemplateId);
            });

            purchaseRuleStates.push({
                ...purchaseRule,
                meta: {
                    ...purchaseRule.meta,
                    addons: shouldIncludeAddons,
                },
                actions: [...filteredActions, ...discountPricingValues],
            });
        }
    }

    return purchaseRuleStates;
};

/**
 * Returns the updated Payload of the partnerPackage / Package purchaseRule
 * Note: This function is being used by both partnerpackages and packages.
 */
export const getPricingTermDiscount = (purchaseRules: PurchaseRule[], purchaseRuleLinks: PurchaseRuleLink[]) => {
    const isBasicTerm = isBasicPricing(purchaseRuleLinks);
    const isAdvancedTerm = isAdvancedPricing(purchaseRuleLinks);

    let termsType = isAdvancedTerm ? TermsType.ADVANCED : TermsType.BASIC;

    const isAnyRulesAdded = some(purchaseRules, ({ actions }) =>
        some(actions, ({ object }) => object !== PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION),
    );
    // if no tag is set and no rules added in case of Migrated Package
    if (!isBasicTerm && !isAdvancedTerm && isAnyRulesAdded) {
        termsType = TermsType.ADVANCED;
    }

    const pricingTerm: BasicDiscountTerm = {
        discount: 0,
        includeAddons: BasicDiscountAddonTerm.INCLUDING_ADD_ONS,
        termType: termsType,
    };

    if (isBasicTerm) {
        const includeAddons = purchaseRuleLinks.some((purchaseRule) =>
            purchaseRule.tags?.some((tag) => tag.id === BasicDiscountAddonTerm.INCLUDING_ADD_ONS),
        );

        const result = purchaseRules[0]?.actions.find(
            ({ shortcut }) => shortcut?.type === PurchaseRuleShortcutType.PERCENT,
        );
        pricingTerm.termType = TermsType.BASIC;
        pricingTerm.discount = Math.abs(result?.shortcut?.amount ?? 0);
        pricingTerm.includeAddons = includeAddons
            ? BasicDiscountAddonTerm.INCLUDING_ADD_ONS
            : BasicDiscountAddonTerm.EXCLUDING_ADD_ONS;
    }

    return pricingTerm;
};
